import { getSlateColorToken } from "@faire/retailer-shared/SlateMappings/getSlateColorToken";
import { Alignment } from "@faire/web-api/indigofair/data/Alignment";
import { IImageElement } from "@faire/web-api/indigofair/data/IImageElement";
import { IImageTile } from "@faire/web-api/indigofair/data/IImageTile";
import { IOverlayElement } from "@faire/web-api/indigofair/data/IOverlayElement";
import { IOptimizeImageOptions } from "@faire/web/common/images/optimizeImages";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import classNames from "classnames";
import React from "react";

import {
  objectFitCoverImageStyles,
  SDUIImage,
} from "@faire/retailer/components/SDUI/__internal__/Image/SDUIImage";
import { SDUIOnClickHandler } from "@faire/retailer/components/SDUI/__internal__/SDUIOnClickHandler/SDUIOnClickHandler";
import * as animationStyles from "@faire/retailer/components/SDUI/__internal__/Styling/__styles__/animationStyles.css";
import { getCssAlignmentValue } from "@faire/retailer/components/SDUI/__internal__/Styling/getCssAlignmentValue";
import { getOpacityHexValue } from "@faire/retailer/components/SDUI/__internal__/Styling/getOpacityHexValue";
import {
  ComponentLoggingEventParams,
  SDUIComponentRenderBehaviour,
} from "@faire/retailer/components/SDUI/__internal__/types";
import * as styles from "@faire/retailer/components/SDUI/ComponentRenderers/Image/styles/SDUIImageElementRendererStyles.css";
import { SDUITypographyRenderer } from "@faire/retailer/components/SDUI/ComponentRenderers/Typography/SDUITypographyRenderer";

import { numberToPixel } from "../../__internal__/Styling/conversions";

type ImageStyles = {
  aspectRatio?: number;
  width?: string;
  height?: string;
  isCircular?: boolean;
};

interface IProps {
  className?: string;
  imageElement: IImageElement;
  loggingData: ComponentLoggingEventParams;
  renderBehaviour?: SDUIComponentRenderBehaviour;
  imageStyles?: ImageStyles;
  optimizeImageOptions?: IOptimizeImageOptions;
  animations?: Array<keyof typeof IImageTile.ImageTileAnimation>;
}
export const SDUIImageElementRenderer = ({
  className,
  imageElement,
  loggingData,
  renderBehaviour,
  imageStyles,
  optimizeImageOptions,
  animations,
}: IProps) => {
  const { image, on_click, overlay } = imageElement;
  if (!image) {
    return null;
  }

  const contentWidth =
    imageStyles?.width ??
    numberToPixel(optimizeImageOptions?.width) ??
    numberToPixel(image.width) ??
    "auto";
  const contentHeight =
    imageStyles?.height ??
    numberToPixel(optimizeImageOptions?.height) ??
    numberToPixel(image.height) ??
    "auto";

  const { alt_text } = image;

  return (
    <SDUIOnClickHandler
      onClickActions={on_click}
      loggingData={loggingData}
      logSDUIClickEvent={on_click.length > 0}
      className={classNames(
        className,
        animationStyles.imageWrapper,
        animations?.includes(IImageTile.ImageTileAnimation.ZOOM)
          ? animationStyles.imageZoomAnimationHoverTarget
          : null,
        animations?.includes(IImageTile.ImageTileAnimation.UNDERLINE)
          ? animationStyles.underlineAnimationHoverTarget
          : null
      )}
      componentStyle={{
        width: contentWidth,
      }}
    >
      <div
        className={classNames(
          styles.pictureOverflowWrapper,
          imageStyles?.isCircular ? styles.circularImageVariant : null
        )}
        style={assignInlineVars({
          [styles.varContentWidth]: contentWidth,
          [styles.varContentHeight]: contentHeight,
          [styles.varAspectRatio]:
            imageStyles?.aspectRatio?.toString() ?? "auto",
        })}
      >
        <SDUIImage
          altText={alt_text || overlay?.text?.text}
          renderBehaviour={renderBehaviour}
          responsiveImage={{
            mobileAndAbove: image,
          }}
          optimizeImageOptions={optimizeImageOptions ?? {}}
          className={classNames(
            styles.imageContainer,
            animationStyles.imageZoom
          )}
          imageStyles={objectFitCoverImageStyles}
        />
        <SDUIOverlayRenderer overlay={overlay} />
      </div>
    </SDUIOnClickHandler>
  );
};

const SDUIOverlayRenderer = ({ overlay }: { overlay?: IOverlayElement }) => {
  if (!overlay || !overlay.text) {
    return null;
  }
  return (
    <div
      className={styles.overlay}
      style={{
        justifyContent: getCssAlignmentValue(
          overlay.style?.horizontal_text_alignment
        ),
        alignItems: getCssAlignmentValue(
          overlay.style?.vertical_text_alignment ?? Alignment.ALIGNMENT_END
        ),
        backgroundColor: overlay?.color
          ? `${getSlateColorToken(overlay?.color)}${getOpacityHexValue(
              overlay?.opacity
            )}`
          : undefined,
      }}
    >
      <SDUITypographyRenderer
        textElement={overlay.text}
        loggingData={undefined}
        // TODO(FD-237289) move this logic to backend
        avoidUnderLineOnHover={true}
      />
    </div>
  );
};
